import { all, call, fork, put, select } from 'redux-saga/effects';
import * as http from 'utils/http';
import config from 'config';
import moment from 'moment';

import { setUserInfo } from './duck';
import { MODIFIED_PRICE_EXTRA, cancellationValues } from 'modules/shipper-order/Form/constants';
import { splitHours } from 'utils/converters';
import { getShipperAddresses } from 'redux/modules/shipper-order-form/selectors';

const ADD_ADDRESS_TO_SHIPPER = true;

const createAddress = (address, url) =>
  http
    .post(`${config.API_URL_V2}addresses/`, address)
    .then((address_created) => http.get(`${config.API_URL}addresses/${address_created.id}/`))
    .then((address) => ({
      ...address,
      tmp_url: url,
    }));

const addAddressToShipper = (address_id, shipper_id) => {
  http
    .put(`${config.API_URL_V2}shippers/${shipper_id}/addresses/${address_id}/`, {})
    .then((address) => ({
      ...address,
    }));
};

function* addAddressesToShipper(addresses, shipper_id) {
  return yield all(addresses.map((address) => addAddressToShipper(address.id, shipper_id)));
}

const parseStandstills = (stops, standstills) =>
  (standstills || [])
    .map((standstill) => {
      if (!standstill) {
        return null;
      }

      const { time_from, time_to, stop_id, ...rest } = standstill || {};

      if (!time_from || !time_to) {
        return null;
      }

      const times_from = splitHours(time_from);
      const times_to = splitHours(time_to);
      const stop = stops[stop_id];
      const stopTimeFrom = stop.time_from;

      const momentFrom = moment(stopTimeFrom).hours(times_from.hours).minutes(times_from.minutes);
      const momentTo = moment(stopTimeFrom).hours(times_to.hours).minutes(times_to.minutes);

      const fromIsValid = momentFrom && momentFrom.isValid && momentFrom.isValid();
      const toIsValid = momentTo && momentTo.isValid && momentTo.isValid();

      if (!fromIsValid || !toIsValid) {
        return null;
      }

      return {
        time_from: momentFrom.format(),
        time_to: momentTo.format(),
        stop_id,
        ...rest,
      };
    })
    .filter((notnull) => notnull);

function* adaptShipperOrder(shipperOrder, { withCreation = true } = {}) {
  const shipperAddresses = yield select(getShipperAddresses);

  const { shipments } = shipperOrder;

  const addressesObject = shipperAddresses.reduce(
    (obj, addr) => ({
      ...obj,
      [addr.url]: addr,
    }),
    {}
  );

  const { shipper = {}, user = {}, stops, standstills } = shipperOrder;

  const newAddressesObject = shipperAddresses
    .filter((address) => address.isNew)
    .reduce(
      (obj, addr) => ({
        ...obj,
        [addr.url]: addr,
      }),
      {}
    );

  const addressesToCreate = shipments
    .reduce((arr, { address_from, address_to }) => [...arr, address_from, address_to], [])
    .filter((val, index, arr) => arr.indexOf(val) === index) // AKA uniq
    .filter((val) => !!newAddressesObject && !!val?.url && !!newAddressesObject[val.url])
    .map(({ url } = {}) => newAddressesObject[url]);

  const createdAddress = withCreation
    ? yield all(addressesToCreate.map((address) => call(createAddress, address, address.url)))
    : [];

  // Add the shipper to all addresses created.
  if (withCreation && addressesToCreate && ADD_ADDRESS_TO_SHIPPER) {
    yield fork(addAddressesToShipper, createdAddress, shipper.id);
  }

  const getAddressObject = (address) => {
    // If the address is an object, we keep the object
    if (typeof address === 'object') {
      return address;
    }

    // If the address is recently created, we use the new object
    const recentlyCreatedAddress = createdAddress.find(
      (newAddress) => newAddress.tmp_url === address
    );
    if (recentlyCreatedAddress) {
      return recentlyCreatedAddress;
    }

    // Otherwise, we assume that the address exist in the address list.
    return addressesObject[address];
  };

  return {
    ...shipperOrder,
    extras: (shipperOrder.extras || []).filter((extra) => extra !== MODIFIED_PRICE_EXTRA),
    shipper: (shipper || {}).url,
    user: (user || {}).url,
    shipments: shipments.map(({ address_from, address_to, ...shipment }) => ({
      ...shipment,
      address_from: getAddressObject(address_from),
      address_to: getAddressObject(address_to),
      dropoff_appointment: shipment.has_dropoff_appointment ? shipment.dropoff_appointment : null,
    })),
    standstills: parseStandstills(stops, standstills),
  };
}

function* setUserSettings(user) {
  const shipperOrderUser = yield call(http.get, user.url);
  if (shipperOrderUser && shipperOrderUser.country_settings) {
    const { zipcode_re: zipcodeRE, country, vat, currency } = shipperOrderUser.country_settings;

    yield put(
      setUserInfo({
        zipcodeRE,
        country,
        vat,
        currency,
      })
    );
  }
}

function getCancellationValues(shipperOrder) {
  if (!shipperOrder) return cancellationValues.DEFAULT;

  const { service_type: serviceType, stops } = shipperOrder;
  const [firstStop] = stops;
  const country = firstStop?.address?.country;

  return cancellationValues[serviceType] && cancellationValues[serviceType][country]
    ? cancellationValues[serviceType][country]
    : cancellationValues.DEFAULT;
}

export {
  adaptShipperOrder,
  setUserSettings,
  parseStandstills,
  addAddressesToShipper,
  getCancellationValues,
};
